import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { LoggerService } from './logger.service';

// Type-only import to avoid bundling the service
import type { CustomSyncService } from './custom-sync.service';

/**
 * Initializes custom sync service
 */
@Injectable({
  providedIn: 'root',
})
export class SyncInitService {
  private injector = inject(Injector);
  private log = inject(LoggerService).getLogger('sync-init');

  // Lazy getter - only inject the service when first accessed via dynamic import
  // These are signals so computed properties can react when they are set
  private readonly _syncService = signal<CustomSyncService | undefined>(undefined);
  private readonly _hasStarted = signal(false); // Track if start() has been called

  // Expose reactive signals for template binding
  readonly showResetDialog = computed(() => {
    if (!this._hasStarted() || !this._syncService()) return false;
    try {
      // Access the underlying service's signal
      return this._syncService()!.showResetDialog();
    } catch {
      // Service not fully initialized yet
      return false;
    }
  });

  /**
   * Expose connectionState directly from the underlying signal.
   * Single level of indirection — avoids multi-hop computed chains
   * that can miss dependency registration before the service loads.
   */
  readonly connectionState = computed(
    () => this._syncService()?.connectionState() ?? 'disconnected',
  );

  /** True when this tab is paused waiting for DB leadership */
  readonly isPaused = computed(() => this._syncService()?.isPaused() ?? false);

  /**
   * True when sync is active — includes both 'connected' (this tab is
   * the WebSocket leader) and 'follower' (another tab is the leader;
   * sync still works via BroadcastChannel).
   */
  readonly connected = computed(() => {
    const state = this.connectionState();
    return state === 'connected' || state === 'follower';
  });

  private async getSyncService(): Promise<CustomSyncService> {
    if (!this._syncService()) {
      this.log.info('📦 Loading CustomSyncService...');
      const { CustomSyncService } = await import('./custom-sync.service');
      this._syncService.set(this.injector.get(CustomSyncService));
      this.log.info('✅ CustomSyncService loaded');
    }
    return this._syncService()!;
  }

  /**
   * Start the custom sync service
   */
  async start(): Promise<void> {
    this.log.info(`🚀 === SYNC-INIT START ===`);

    this._hasStarted.set(true); // Mark as started before accessing service
    const service = await this.getSyncService();

    this.log.info('🚀 Starting custom sync');
    await service.start();

    this.log.info(`✅ Sync start() complete`);
  }

  /**
   * Stop custom sync service
   */
  async stop(): Promise<void> {
    if (!this._hasStarted()) return; // Don't stop if never started
    const service = await this.getSyncService();
    service.stop();
  }

  /**
   * Check if syncing (delegates to active service)
   */
  async syncing(): Promise<boolean> {
    if (!this._hasStarted()) return false; // Not started = not syncing
    const service = await this.getSyncService();
    return service.syncing();
  }

  /**
   * Get sync error (delegates to active service)
   */
  error(): string | null {
    if (!this._hasStarted()) {
      this.log.debug('error() called but service not started - returning null');
      return null;
    }
    if (!this._syncService()) {
      return null;
    }
    const errorValue = this._syncService()!.error();
    this.log.debug(`error() returning: ${errorValue}`);
    return errorValue;
  }

  /**
   * Check if paused (non-leader tab waiting for DB leadership)
   */
  paused(): boolean {
    return this._syncService()?.isPaused() ?? false;
  }

  /**
   * Get last sync time
   * Custom sync doesn't track last sync time - always returns null
   */
  async lastSyncTime(): Promise<Date | null> {
    return null;
  }

  /**
   * Check if connected (legacy method for backward compatibility)
   */
  isConnected(): boolean {
    const connectedValue = this.connected();
    this.log.debug(`isConnected() returning: ${connectedValue}`);
    return connectedValue;
  }

  /**
   * Pause sync
   * Custom sync doesn't support pause - no-op
   */
  pause(): void {
    this.log.warn('Pause not supported in custom sync');
  }

  /**
   * Resume sync
   * Custom sync doesn't support resume - no-op
   */
  resume(): void {
    this.log.warn('Resume not supported in custom sync');
  }

  /**
   * Connect (alias for start, for backward compatibility)
   */
  async connect(): Promise<void> {
    return this.start();
  }
}
